import React from "react";
import IconButton from "@mui/material/IconButton";
import {
    DeleteForeverRounded,
    DescriptionRounded,
    DownloadRounded,
    HistoryRounded,
    IntegrationInstructionsRounded,
    RefreshRounded,
    RequestQuoteSharp
} from "@mui/icons-material";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import {Tooltip, useTheme} from "@mui/material";
import {useInvoiceRepository} from "../../../api/repositories/InvoiceRepository";
import {useNavigate} from "react-router-dom";
import {ID_ACCOUNTED, ID_REISSUED, ID_TO_SEND, ID_UNACCOUNTED} from "../constants/InvoicesStatuses";
import DebugXMLInvoice from "../../../experiments/DebugXMLInvoice";
import {useTranslation} from 'react-i18next';
import {ID_CREDIT_NOTE, ID_DEBIT_NOTE, ID_INVOICE} from "../constants/InvoicesTypes";
import {useSapRepository} from "../../../api/repositories/SapRepository";
import {useHasScope} from "../../../../../config/globalConfig";
import {
    DELETE_RECEIVABLE,
    DELETE_RECEIVABLE_LINKED_DOCUMENT,
    DOWNLOAD_RECEIVABLE_PDF,
    DOWNLOAD_RECEIVABLE_PDF_LINKED_DOCUMENT,
    DOWNLOAD_RECEIVABLE_XML,
    PAYMENT_REQUEST_VIEW,
    RECEIVABLE_LINKED_DOCUMENT_DETAIL,
    REISSUE_RECEIVABLE,
    REPROCESS_RECEIVABLE,
    VIEW_RECEIVABLE_DETAIL
} from "../../../utils/FinanceScope";

export enum InvoicePageType {
    INVOICE,
    LINKED,
}

const InvoiceTableActions = (props: {
    page: InvoicePageType;
    id: string;
    invoiceId: string;
    invoiceStatus: string;
    invoiceType?: string;
    paymentMethod?: string;
    setWarningOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setWarningTitle: React.Dispatch<React.SetStateAction<string>>;
    setOpenActionDialog?: React.Dispatch<React.SetStateAction<boolean>>;
    setActionDialogId?: React.Dispatch<React.SetStateAction<string>>;
    setActionDialogType?: React.Dispatch<React.SetStateAction<string>>;
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const hasScope = useHasScope;
    const { downloadPDF } = useInvoiceRepository();
    const { getPreview } = useSapRepository();

    const [openXML, setOpenXML] = React.useState(false);
    const [xml, setXml] = React.useState<string>("");

    const { t } = useTranslation('finance', { keyPrefix: 'invoices' });


    const handleOpenXML = () => {
        getPreview(props.id).then((it) => {
            setXml(it);
            setOpenXML(true);
        });
    };

    const handleCloseXML = () => {
        setXml("")
        setOpenXML(false);
    };


    function showInvoice() {

        if (!hasScope(DELETE_RECEIVABLE) && !hasScope(DOWNLOAD_RECEIVABLE_PDF) && !hasScope(DOWNLOAD_RECEIVABLE_XML) && !hasScope(REISSUE_RECEIVABLE) && !hasScope(REPROCESS_RECEIVABLE) && !hasScope(VIEW_RECEIVABLE_DETAIL)){
            return null; 
        }

        return (
                <>
                    <FinanceDynamicActions>
                        { hasScope(VIEW_RECEIVABLE_DETAIL) && 
                        <Tooltip arrow title={t('tables.tooltips.show-detail')}>
                            <IconButton
                                    sx={{ color: theme.palette.accent.main }}
                                    onClick={() => window.open(`/finance/invoices/${props.id}`, "_blank")}
                            >
                                <DescriptionRounded/>
                            </IconButton>
                        </Tooltip>
                        }
                        { hasScope(DOWNLOAD_RECEIVABLE_PDF) &&
                        <Tooltip arrow title={t('tables.tooltips.download-pdf')}>
                            <IconButton
                                    sx={{ color: theme.palette.accent.main }}
                                    onClick={() => {
                                        downloadPDF(props.id).then((res) => {
                                            if (res?.result) {
                                                let pdfWindow = window.open("");
                                                pdfWindow!.document.write(
                                                        `<iframe width='100%' height='100%' src='data:application/pdf;base64, ${encodeURI(
                                                                res.result
                                                        )}'/>`
                                                );
                                            }
                                        });
                                    }}
                            >
                                <DownloadRounded/>
                            </IconButton>
                        </Tooltip>
                        }
                        { hasScope(DOWNLOAD_RECEIVABLE_XML) &&
                        <Tooltip arrow title={t('tables.invoice.actions.tooltips.xml')}>
                            <IconButton
                                    sx={{ color: theme.palette.accent.main }}
                                    onClick={handleOpenXML}
                            >
                                <IntegrationInstructionsRounded/>
                            </IconButton>
                        </Tooltip>
                        }
                        { hasScope(PAYMENT_REQUEST_VIEW) &&
                            <Tooltip arrow title={t('tables.tooltips.showPayment')}>
                                <IconButton
                                    sx={{color: theme.palette.accent.main}}
                                    disabled={props.paymentMethod !== 'J' && props.invoiceStatus !== ID_ACCOUNTED && props.invoiceStatus !== ID_REISSUED}
                                    onClick={() => {
                                        window.open(`/finance/payments/payment-request?invoiceNumber=${props.invoiceId}`, "_blank");
                                    }}
                                >
                                    <RequestQuoteSharp/>
                                </IconButton>
                            </Tooltip>
                        }
                        { hasScope(REISSUE_RECEIVABLE) &&
                        <Tooltip arrow title={t('tables.tooltips.reissue')}>
                            <IconButton
                                    disabled={(props.invoiceStatus !== ID_ACCOUNTED) || props.invoiceType == ID_CREDIT_NOTE}
                                    sx={{ color: theme.palette.accent.main }}
                                    onClick={() => {
                                        props.setOpenActionDialog?.(true);
                                        props.setActionDialogId?.(props.id);
                                        props.setActionDialogType?.('reissue');
                                    }}
                            >
                                <RefreshRounded/>
                            </IconButton>
                        </Tooltip>
                        }
                        { hasScope(REPROCESS_RECEIVABLE) &&
                        <Tooltip arrow title={t('tables.tooltips.reprocess')}>
                            <IconButton
                                    disabled={(props.invoiceStatus !== ID_TO_SEND && props.invoiceStatus !== ID_UNACCOUNTED) || props.invoiceType !== ID_INVOICE}
                                    sx={{ color: theme.palette.accent.main }}
                                    onClick={() => {
                                        props.setOpenActionDialog?.(true);
                                        props.setActionDialogId?.(props.id);
                                        if (props.invoiceStatus == ID_UNACCOUNTED){
                                            props.setActionDialogType?.('reprocess-unaccounted');
                                        } else {
                                            props.setActionDialogType?.('reprocess');
                                        }
                                        
                                    }}
                            >
                                <HistoryRounded/>
                            </IconButton>
                        </Tooltip>
                        }
                        { hasScope(DELETE_RECEIVABLE) &&
                        <Tooltip arrow title={t("tables.tooltips.delete")}>
                            <IconButton
                                    disabled={props.invoiceStatus !== ID_TO_SEND}
                                    sx={{ color: theme.palette.accent.main }}
                                    onClick={() => {
                                        props.setWarningTitle(props.id);
                                        props.setWarningOpen(true);
                                    }}
                            >
                                <DeleteForeverRounded/>
                            </IconButton>
                        </Tooltip>
                        }
                    </FinanceDynamicActions>
                    <DebugXMLInvoice xml={xml} open={openXML} onClose={handleCloseXML}></DebugXMLInvoice>
                </>
        );
    }

    function showLinked() {
        if (!hasScope(RECEIVABLE_LINKED_DOCUMENT_DETAIL) && !hasScope(DOWNLOAD_RECEIVABLE_PDF_LINKED_DOCUMENT) && !hasScope(DELETE_RECEIVABLE_LINKED_DOCUMENT)){
            return null;
        }

        return (
                <FinanceDynamicActions>
                    {hasScope(RECEIVABLE_LINKED_DOCUMENT_DETAIL) && 
                    <Tooltip arrow title={t('tables.tooltips.show-detail')}>
                        <IconButton
                                sx={{ color: theme.palette.accent.main }}
                                onClick={() => {
                                    window.open(`/finance/invoices/${props.id}`, "_blank");
                                    window.location.reload();
                                }}
                        >
                            <DescriptionRounded/>
                        </IconButton>
                    </Tooltip>
                    }
                    {hasScope(DOWNLOAD_RECEIVABLE_PDF_LINKED_DOCUMENT) && 
                    <Tooltip arrow title={t('tables.tooltips.download-pdf')}>
                        <IconButton
                                sx={{ color: theme.palette.accent.main }}
                                onClick={() => {
                                    downloadPDF(props.id).then((res) => {
                                        if (res?.result) {
                                            let pdfWindow = window.open("");
                                            pdfWindow!.document.write(
                                                    `<iframe width='100%' height='100%' src='data:application/pdf;base64, ${encodeURI(
                                                            res.result
                                                    )}'></iframe>`
                                            );
                                        }
                                    });
                                }}
                        >
                            <DownloadRounded/>
                        </IconButton>
                    </Tooltip>
                    }
                    {hasScope(DELETE_RECEIVABLE_LINKED_DOCUMENT) && 
                    <Tooltip arrow title={t("tables.tooltips.delete")}>
                        <IconButton
                                disabled={props.invoiceStatus !== ID_TO_SEND}
                                sx={{ color: theme.palette.accent.main }}
                                onClick={() => {
                                    props.setWarningTitle(props.id);
                                    props.setWarningOpen(true);
                                }}
                        >
                            <DeleteForeverRounded/>
                        </IconButton>
                    </Tooltip>
                }
                </FinanceDynamicActions>
        );
    }

    return (
            <>
                {props.page === InvoicePageType.INVOICE ? showInvoice() : showLinked()}
            </>
    );
};

export default InvoiceTableActions;
